import React, { memo } from 'react';
import { colors } from '@sato/components';
import { getDaysInMonth } from 'date-fns';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryLegend,
  VictoryStack,
  VictoryTooltip,
  VictoryVoronoiContainer,
  VictoryZoomContainer,
} from 'victory';

import {
  WaterUsageAmounts,
  WaterUsageData,
  WaterUsageMeasurement,
} from '@models/waterUsage.types';
import { localizationStore, t } from '@stores/localization';

import { coldWaterColor, warmWaterColor } from './chartTheme';
import {
  baseLabelStyles,
  chartRem,
  isMobileChartSize,
  waterUsageChartTheme,
} from './waterUsage.theme';
import {
  BOLD_AMOUNT_MARKER,
  getChartTooltipText,
} from './WaterUsageChart.utils';

/**
 * VictoryLabel renders one <tspan> per text line, with no way to vary
 * font-weight within a line via the style prop. This tspan replacement
 * splits its line on BOLD_AMOUNT_MARKER and wraps everything after it
 * (the amount) in a nested, bold <tspan>, while leaving VictoryLabel's own
 * line positioning/styling untouched.
 */
export const BoldAmountTSpan = ({
  children,
  ...tspanProps
}: React.SVGProps<SVGTSpanElement>) => {
  if (typeof children !== 'string' || !children.includes(BOLD_AMOUNT_MARKER)) {
    return <tspan {...tspanProps}>{children}</tspan>;
  }

  const [label, amount] = children.split(BOLD_AMOUNT_MARKER);

  return (
    <tspan {...tspanProps}>
      {label}
      <tspan style={{ fontWeight: 'bold' }}>{amount}</tspan>
    </tspan>
  );
};

export interface WaterUsageChartDataPoint
  extends Partial<WaterUsageMeasurement> {
  usage: WaterUsageAmounts;
  /** Day of month */
  day: number;
}

export interface ChartProps {
  measurements: WaterUsageChartDataPoint[];
  yRangeMax: number;
}

/* Making responsive chart was difficult with the library, lot of manual style changes.
 * On mobile version, the tooltip works only for the upper bar which requires a
 * VictoryVoronoiContainer to work and behaves buggily with ZoomContainer although
 * formally supported: https://commerce.nearform.com/open-source/victory/docs/create-container */
export const WaterUsageChart = memo(({ data }: { data: WaterUsageData }) => {
  // Fill 0 results for days without measurements
  const measurements: WaterUsageChartDataPoint[] = [
    ...Array(getDaysInMonth(data.period.start)).keys(),
  ].map((index) => {
    const dayOfMonth = index + 1;
    const measurement = data.items.find(
      (meas) => meas.date.getDate() === dayOfMonth
    );

    return {
      ...measurement,
      day: dayOfMonth,
      usage: measurement?.usage ?? {
        cold: 0,
        warm: 0,
        total: 0,
        costs: { cold: 0, warm: 0, total: 0 },
      },
    };
  });

  const totalUsageLiters = measurements.map(
    (measurement) => measurement.usage.total
  );

  const highestLiterAmount = Math.max(...totalUsageLiters);

  const yRangeGap = 50;
  const yRangeMax = Math.ceil(highestLiterAmount / yRangeGap) * yRangeGap;

  return (
    <div className="w-full">
      <VictoryChart
        theme={waterUsageChartTheme}
        style={{ parent: { overflow: 'visible' } }}
        // These won't work if you abstract into separate component
        containerComponent={
          isMobileChartSize ? (
            <VictoryZoomContainer
              zoomDimension="x"
              zoomDomain={{
                x: [0, 12],
              }}
              allowZoom={false}
            />
          ) : (
            <VictoryVoronoiContainer voronoiDimension="x" />
          )
        }
      >
        <VictoryLegend
          data={[
            {
              name: t('waterUsageColdWater'),
              symbol: { fill: coldWaterColor, type: 'square' },
            },
            {
              name: t('waterUsageWarmWater'),
              symbol: { fill: warmWaterColor, type: 'square' },
            },
          ]}
          x={chartRem(13)}
        />

        <VictoryAxis
          label={t('day')}
          // We do not want to show day value on the x axis due to measurement values not exactly corresponding to a full day.
          // A days measurement is taken around 3-6am
          tickLabelComponent={<></>}
          axisLabelComponent={
            <VictoryLabel
              // pixel perfect horizontal align relative to whole chart width
              x={
                waterUsageChartTheme.chart.width / 2 -
                waterUsageChartTheme.chart.padding.left / 2
              }
            />
          }
          tickValues={measurements.map((mes) => mes.day)}
        />

        <VictoryAxis
          label={t('liters')}
          tickLabelComponent={<VictoryLabel dx={chartRem(-2)} />}
          axisLabelComponent={<VictoryLabel y={chartRem(4.3)} x={0} />}
          domain={[0, yRangeMax]}
          tickCount={5}
          dependentAxis
          crossAxis={false} // make zero value tick visible
        />

        <VictoryStack
          style={{
            data: {
              stroke: ({ datum }) =>
                !(datum as WaterUsageChartDataPoint).usage.total
                  ? 'transparent'
                  : 'white',
              strokeWidth: ({ datum }) =>
                !(datum as WaterUsageChartDataPoint).usage.total
                  ? 0
                  : chartRem(0.3),
            },
          }}
          labels={(data) =>
            getChartTooltipText({
              waterUsageData: data.datum,
              locale: localizationStore.language,
              t,
            })
          }
          labelComponent={
            <VictoryTooltip
              constrainToVisibleArea
              labelComponent={
                <VictoryLabel
                  // Line order here must stay in sync with the text lines
                  // returned by getChartTooltipText: warm (label+amount,
                  // range), cold (label+amount, range), total.
                  tspanComponent={<BoldAmountTSpan />}
                  style={[
                    {
                      ...baseLabelStyles,
                      fontSize: chartRem(3),
                      // The "no results" message is rendered as a single
                      // line, which always takes on this first style slot —
                      // keep it black instead of the warm-water color.
                      fill: ({
                        datum,
                      }: {
                        datum?: WaterUsageChartDataPoint;
                      }) =>
                        datum?.timeStamp
                          ? colors.brand.primary.dark
                          : baseLabelStyles.fill,
                    },
                    {
                      ...baseLabelStyles,
                      fontSize: chartRem(3),
                    },
                    {
                      ...baseLabelStyles,
                      fontSize: chartRem(3),
                      fill: colors.brand.interactive.main,
                    },
                    {
                      ...baseLabelStyles,
                      fontSize: chartRem(3),
                    },
                    {
                      ...baseLabelStyles,
                      fontWeight: 'bold',
                      fontSize: chartRem(4),
                    },
                  ]}
                  lineHeight={[1.4, 2, 1.4, 2, 1.6]}
                />
              }
            />
          }
        >
          <VictoryBar data={measurements} x="day" y="usage.warm" />

          <VictoryBar data={measurements} x="day" y="usage.cold" />
        </VictoryStack>
      </VictoryChart>
    </div>
  );
});
